import { fittingUpgradeTargetSchema } from "@frontier/content/ship-fitting";
import { z } from "zod";
import { CLIENT_UPDATE_REQUIRED } from "@frontier/protocol/client-compatibility";
import {
  equipmentKeySchema,
  equipmentTierSchema,
  upgradePriceVersionSchema,
} from "@frontier/protocol/contracts";
import type { Profile } from "@frontier/protocol/contracts";
import { ApiError, api } from "./session-api";

const commandSchema = z.discriminatedUnion("operation", [
  z
    .object({
      operation: z.literal("craft"),
      key: equipmentKeySchema,
      requestId: z.string().min(1),
    })
    .strict(),
  z
    .object({
      operation: z.literal("dismantle"),
      key: equipmentKeySchema,
      tier: equipmentTierSchema,
      requestId: z.string().min(1),
    })
    .strict(),
  z
    .object({
      operation: z.literal("upgrade"),
      key: equipmentKeySchema,
      fromTier: equipmentTierSchema,
      requestId: z.string().min(1),
      priceVersion: upgradePriceVersionSchema.optional(),
      mount: fittingUpgradeTargetSchema.optional(),
    })
    .strict(),
]);
export type ArsenalIntent = z.infer<typeof commandSchema>;
export type ArsenalCommand =
  | Omit<Extract<ArsenalIntent, { operation: "craft" }>, "requestId">
  | Omit<Extract<ArsenalIntent, { operation: "dismantle" }>, "requestId">
  | (Omit<
      Extract<ArsenalIntent, { operation: "upgrade" }>,
      "requestId" | "priceVersion"
    > & { priceVersion: string });
type Storage = Pick<globalThis.Storage, "getItem" | "setItem" | "removeItem">;
type Client = Pick<
  typeof api,
  "craftEquipment" | "dismantleEquipment" | "upgradeEquipment" | "profile"
>;
export interface ArsenalOutcome {
  status: "confirmed" | "rejected" | "uncertain";
  profile: Profile | null;
  message: string;
  authExpired?: boolean;
  priceChanged?: boolean;
}
function targetIdentity(command: ArsenalCommand | ArsenalIntent): string {
  return `${command.operation}:${command.key.kind}:${command.key.id}:${command.operation === "upgrade" ? command.fromTier : command.operation === "dismantle" ? command.tier : 0}${command.operation === "upgrade" && command.mount ? `:${command.mount.hullId}:${command.mount.slot.kind}:${command.mount.slot.index}` : ""}`;
}
function identity(command: ArsenalCommand | ArsenalIntent): string {
  return `${targetIdentity(command)}${command.operation === "upgrade" ? (command.priceVersion === undefined ? ":legacy" : `:price=${command.priceVersion}`) : ""}`;
}

export class ArsenalOperations {
  private readonly players = new Map<string, ArsenalIntent[]>();
  constructor(
    private readonly storage: Storage,
    private readonly createId: () => string,
  ) {}

  pending(playerId: string): readonly ArsenalIntent[] {
    const cached = this.players.get(playerId);
    if (cached) return cached;
    let items: ArsenalIntent[] = [];
    try {
      const serialized = this.storage.getItem(this.storageKey(playerId));
      if (serialized)
        items = z.array(commandSchema).max(64).parse(JSON.parse(serialized));
    } catch (error) {
      console.warn("arsenal-pending-data-invalid", {
        playerId,
        message:
          error instanceof Error ? error.message : "Invalid stored operation",
      });
      this.storage.removeItem(this.storageKey(playerId));
    }
    this.players.set(playerId, items);
    return items;
  }

  begin(playerId: string, command: ArsenalCommand): ArsenalIntent {
    const pending = this.pending(playerId);
    const prior = pending.find(
      (item) => targetIdentity(item) === targetIdentity(command),
    );
    if (prior) return prior;
    if (pending.length >= 64)
      throw new Error("Сначала проверьте незавершённые операции арсенала");
    const intent: ArsenalIntent = { ...command, requestId: this.createId() };
    this.persist(playerId, [...pending, intent]);
    return intent;
  }

  async execute(
    playerId: string,
    token: string,
    intent: ArsenalIntent,
    client: Client = api,
    current: () => boolean = () => true,
  ): Promise<ArsenalOutcome> {
    if (
      !this.pending(playerId).some(
        (item) =>
          item.requestId === intent.requestId &&
          identity(item) === identity(intent),
      )
    )
      return {
        status: "rejected",
        profile: null,
        message: "Эта операция уже проверена",
      };
    try {
      const profile = await (intent.operation === "craft"
        ? client.craftEquipment(token, intent.key, intent.requestId)
        : intent.operation === "dismantle"
          ? client.dismantleEquipment(
              token,
              intent.key,
              intent.tier,
              intent.requestId,
            )
          : client.upgradeEquipment(
              token,
              intent.key,
              intent.fromTier,
              intent.requestId,
              intent.priceVersion,
              intent.mount,
            ));
      if (!current())
        return {
          status: "uncertain",
          profile: null,
          message:
            "Операция относится к прежней сессии. Проверь её после входа.",
        };
      if (profile.playerId !== playerId)
        throw new Error("Ответ арсенала относится к другому пилоту");
      this.complete(playerId, intent);
      return {
        status: "confirmed",
        profile,
        message:
          intent.operation === "craft"
            ? "Копия MK I добавлена на склад."
            : intent.operation === "dismantle"
              ? "Копия разобрана. Компоненты возвращены."
              : "Один экземпляр улучшен.",
      };
    } catch (error) {
      if (!current())
        return {
          status: "uncertain",
          profile: null,
          message: "Проверь операцию с действующей сессией.",
        };
      const rejected =
        error instanceof ApiError &&
        error.status >= 400 &&
        error.status < 500 &&
        error.status !== 401 &&
        error.code !== CLIENT_UPDATE_REQUIRED &&
        error.status !== 408 &&
        error.status !== 429;
      if (rejected) this.complete(playerId, intent);
      return {
        status: rejected ? "rejected" : "uncertain",
        profile: null,
        ...(error instanceof ApiError && error.status === 401
          ? { authExpired: true }
          : {}),
        ...(error instanceof ApiError && error.code === "PRICE_CHANGED"
          ? { priceChanged: true }
          : {}),
        message:
          error instanceof ApiError && error.code === CLIENT_UPDATE_REQUIRED
            ? error.message
            : rejected
              ? error.message
              : "Результат операции неизвестен. Нажми «Проверить операцию».",
      };
    }
  }

  private complete(playerId: string, intent: ArsenalIntent): void {
    this.persist(
      playerId,
      this.pending(playerId).filter(
        (item) => item.requestId !== intent.requestId,
      ),
    );
  }
  private storageKey(playerId: string): string {
    return `frontier.arsenal.pending.${playerId}`;
  }
  private persist(playerId: string, items: ArsenalIntent[]): void {
    if (items.length)
      this.storage.setItem(this.storageKey(playerId), JSON.stringify(items));
    else this.storage.removeItem(this.storageKey(playerId));
    this.players.set(playerId, items);
  }
}
